import React, { useCallback, useEffect, useMemo } from 'react';
import { Grid } from '@mui/material';
import { WidgetThemeProvider } from '../theme/Theme';
import { deepOmitNull } from '../utils';
import RotatingLoader from '../../ui/RotatingLoader';
import { OrderPageContext } from './useOrderPage';
import { useInitPassOrderPageState } from './initPassOrderPageState';
import { setOrderPageStateToLocalStorage } from './orderPageStateLocalStorage';
import { useOrderPageProviderBase } from './useOrderPageProviderBase';
import { useAccountData } from '../../storefront/StorefrontThemeWrapper';

const noop = () => {};

const defaultTheme = {
  palette: {
    primary: '#4abd00',
    trackFill: 'rgba(74,189,0,0.63)',
    railFill: 'rgba(74,189,0,0.63)',
    handle: '#1e6203',
    text: {
      button: {
        outlined: '#4abd00',
      },
    },
    border: '#4abd00',
    button: {
      outlined: '#4abd00',
    },
  },
};

type PassOrderPageProviderProps = {
  children: React.ReactNode;
  storefrontTheme?: any;
};

export const PassOrderPageProvider = ({ children, storefrontTheme }: PassOrderPageProviderProps) => {
  const account = useAccountData();
  const {
    orderPageState,
    setOrderPageState,
    shouldUseDefaultTheme,
    isLoadingLanguage,
    setPersonalDetails,
    setPaymentUrl,
    setTip,
    setActiveStep,
    setActiveStepAndOrderPageIntent,
    disclaimersAccepted,
    disclaimersError,
    setDisclaimersError,
    onDisclaimersChange,
    sessionTtlMilliseconds,
    setSessionTtlMilliseconds,
  } = useOrderPageProviderBase();

  useInitPassOrderPageState({ orderPageState, setOrderPageState, account: account! });

  useEffect(() => {
    if (orderPageState.isInitialized && orderPageState.orderPage?.id) {
      setOrderPageStateToLocalStorage(orderPageState.orderPage.id, orderPageState);
    }
  }, [orderPageState]);

  const reloadDates = useCallback(noop, []);
  const setOrderDetailValue = useCallback(noop, []);
  const setCustomOrderDetailValue = useCallback(noop, []);
  const setOrderCustomDetails = useCallback(noop, []);
  const setMultipleTicketsDetails = useCallback(noop, []);
  const setDate = useCallback(noop, []);
  const setTime = useCallback(noop, []);

  const ctx = useMemo(
    () => ({
      reloadDates,
      orderPageState,
      setOrderDetailValue,
      setCustomOrderDetailValue,
      setOrderCustomDetails,
      setMultipleTicketsDetails,
      setOrderPageState,
      setPersonalDetails,
      setDate,
      setTime,
      setPaymentUrl,
      setActiveStep,
      setActiveStepAndOrderPageIntent,
      setTip,
      disclaimersAccepted,
      disclaimersError,
      setDisclaimersError,
      onDisclaimersChange,
      sessionTtlMilliseconds,
      setSessionTtlMilliseconds,
    }),
    [
      orderPageState,
      setOrderPageState,
      setPersonalDetails,
      setPaymentUrl,
      setActiveStep,
      setActiveStepAndOrderPageIntent,
      setTip,
      disclaimersAccepted,
      disclaimersError,
      setDisclaimersError,
      onDisclaimersChange,
      sessionTtlMilliseconds,
      setSessionTtlMilliseconds,
    ],
  );

  if (!orderPageState.isInitialized || isLoadingLanguage) {
    return (
      <Grid container minHeight={300}>
        <RotatingLoader />
      </Grid>
    );
  }

  const iframeHeight = document.body.offsetHeight;
  const iframeWidth = document.body.offsetWidth;
  window.parent.postMessage({ height: iframeHeight, width: iframeWidth }, '*');

  const externalTheme = storefrontTheme || deepOmitNull(orderPageState.account.custom_theme);
  return (
    <WidgetThemeProvider remoteThemeOverride={shouldUseDefaultTheme ? defaultTheme : externalTheme}>
      <OrderPageContext.Provider value={ctx}>{children}</OrderPageContext.Provider>
    </WidgetThemeProvider>
  );
};
